import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { difference } from 'lodash'
import Analytics from 'utils/googleAnalytics'

import { CardType, defaultColorLabel, EDH_FORMATS, FormatType, getCategoryName } from 'types/deck'
import { Color } from 'services/apiTypes/card.types'

import { store } from 'app-redux/store'
import { selectCommanderCards } from 'app-redux/deck/selectors'
import { useAppSelector } from 'app-redux/hooks'

import { DeckListResponse, EstimatedBudget } from 'services/apiTypes/deck.types'
import { packageTabKey } from 'services/accountSettings.service'

import SaveService from 'services/save.service'
import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import PackageListItem from 'components/deckPage/modals/cardPackages/ListItem'
import Accordion from 'components/elements/Accordion'
import LineLoader from 'components/elements/LineLoader'
import FormatDropdown from 'components/cardSearchPanel/searchFormElements/formatDropdown'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import TabContainer from 'components/elements/TabButtons'
import Icon from 'components/elements/Icon'
import Checkbox from 'components/formElements/Checkbox'
import PhatDropdown from 'components/formElements/PhatDropdown'
import PhatInput from 'components/formElements/PhatInput'

import { getQueryParamsBySettings } from 'utils/Search'
import { getCardsColorIdenty } from 'utils/cardHelpers'
import { responseToCard } from 'utils/ResponseToResults'

import styles from './packageSelectorList.module.scss'

type Props = {
  open: boolean
  onClose: () => void
  onSelectPackage: (deckId: number | null) => void
}

const PackageSelectorList = ({ open, onClose, onSelectPackage }: Props) => {
  const [loading, setLoading] = useState(false)
  const [packages, setPackages] = useState<DeckListResponse>()
  const [searchValue, setSearchValue] = useState('')

  const [orderBy, setOrderBy] = useState('-importCount')
  const [intendedFormat, setIntendedFormat] = useState<FormatType>()
  const [maxBudget, setMaxBudget] = useState<EstimatedBudget>(0)

  const format = useAppSelector(state => state.deck.format)
  const colorLabels = useAppSelector(state => state.deck.colorLabels)

  const [importIntoCategory, setImportIntoCategory] = useState<'maintain' | 'auto' | 'maybeboard'>('maintain')
  const [addOnlyColorIdentOfCommander, setAddOnlyColorIdentOfCommander] = useState(true)
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  const [importIntoColorTag, setImportIntoColorTag] = useState(colorLabels[0]?.name || '')
  const [forceQuantityToOne, setForceQuantityToOne] = useState(EDH_FORMATS.includes(format))

  const [{ tbUser: username, [packageTabKey]: selectedTab = 'default' }, setCookies] = useCookies([
    'tbUser',
    packageTabKey,
  ])

  useEffect(() => {
    if (!open) return

    setSearchValue('')
    handleFetchPackages()
  }, [open, selectedTab])

  const handleFetchPackages = (queryString = '') => {
    setLoading(true)
    setPackages({ results: [], count: 0, next: '' })

    const params = getQueryParamsBySettings(selectedTab, queryString, username, orderBy, intendedFormat, maxBudget)

    return DeckService.listV3(params)
      .then(res => {
        if (selectedTab !== 'followed') return setPackages(res)

        setPackages({
          ...res,
          results: res.results.map(deck => ({ ...deck, bookmarked: true })),
        })
      })
      .catch(err => ToastService.create(err, 'Card packages'))
      .finally(() => setLoading(false))
  }

  const handleAddCardsToDeck = (deckId: number) => {
    if (!deckId) return

    setLoading(true)

    DeckService.details(deckId, { isPackageImport: true })
      .then(res => {
        const state = store.getState()

        // prettier-ignore
        const cardsInDeck: Record<string, boolean> = Object.values(state.deck.cardMap).reduce((acc, card) => ({ ...acc, [card.oracleCardId]: true  }), {})
        const commanders = selectCommanderCards(state)
        const commandersColorIdent = commanders.reduce<Color[]>(getCardsColorIdenty, new Array<Color>())

        const packageCards = res.cards.map(deckCard => ({
          ...responseToCard(deckCard.card, deckCard),
          categories: importIntoCategory === 'auto' ? [] : deckCard.categories, // Clearing categories if not maintaining, allow default category function to take over
          deckRelationId: '', // Force off the deckRelationId to unlink the cards from the package
        }))

        const cards: CardType[] = packageCards
          .filter(card => {
            const colorDifference = difference(card.colorIdentity, commandersColorIdent).length !== 0

            if (skipDuplicates && cardsInDeck[card.oracleCardId]) return false
            if (addOnlyColorIdentOfCommander && commanders.length && colorDifference) return false

            return true
          })
          .map(card => ({
            ...card,
            qty: forceQuantityToOne ? 1 : card.qty,
            colorLabel: colorLabels.find(tag => tag.name === importIntoColorTag) || defaultColorLabel,
            categories:
              importIntoCategory === 'maintain'
                ? card.categories || []
                : importIntoCategory === 'auto'
                  ? [getCategoryName(card)]
                  : ['Maybeboard'],
          }))

        SaveService.save(cards).then(() => {
          Analytics.signalEvent(`Added package to existing deck`)
        })
      })
      .catch(() => ToastService.create('Unable to fetch card package', 'Card packages'))
      .finally(() => setLoading(false))
  }

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <LineLoader className={`${styles.loader} ${loading ? styles.showLoader : ''}`} fixed />

      <div className={styles.header}>
        <h3>
          <Icon name="cube" /> Package Selection
        </h3>
      </div>

      <TabContainer
        className={styles.tabContainer}
        optionsClassName={styles.tabButton}
        options={[
          {
            label: 'Default',
            selected: selectedTab === 'default',
            onClick: () =>
              setCookies(packageTabKey, 'default', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'default' ? styles.activeTab : '',
          },
          {
            label: 'Mine',
            selected: selectedTab === 'mine',
            onClick: () =>
              setCookies(packageTabKey, 'mine', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'mine' ? styles.activeTab : '',
          },
          {
            label: 'Bookmarked',
            selected: selectedTab === 'followed',
            onClick: () =>
              setCookies(packageTabKey, 'followed', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'followed' ? styles.activeTab : '',
          },
          {
            label: 'All',
            selected: selectedTab === 'search',
            onClick: () =>
              setCookies(packageTabKey, 'search', {
                path: '/',
                expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 40),
              }),
            className: selectedTab === 'search' ? styles.activeTab : '',
          },
        ]}
      />

      <div className={styles.searchFilters}>
        <form
          className={styles.form}
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault()

            handleFetchPackages(searchValue)
          }}>
          <PhatInput
            value={searchValue}
            onChange={setSearchValue}
            containerClassName={styles.searchInput}
            placeholder="Mana ramp, etc"
          />
          <button type="submit">
            <Icon name="arrow right" />
          </button>
        </form>

        <Accordion label="Search Options">
          <PhatDropdown
            inlineHeader
            header="Order card packages by"
            options={[
              { id: '-importCount', label: 'Times imported', onClick: () => setOrderBy('-importCount') },
              { id: 'name', label: 'Package Name', onClick: () => setOrderBy('name') },
              { id: '-updatedAt', label: 'Edited time', onClick: () => setOrderBy('-updatedAt') },
            ]}
            value={orderBy}
          />
          <label>Intended format</label>
          <FormatDropdown
            placeholder="Not all packages have intended formats"
            value={intendedFormat}
            onChange={deckFormat => setIntendedFormat(deckFormat)}
          />

          <label></label>
          <PhatDropdown
            inlineHeader
            header="Maximum budget"
            placeholder="Choose maximum budget"
            options={[
              { id: '0', label: 'Any', onClick: () => setMaxBudget(0) },
              { id: '1', label: '$', onClick: () => setMaxBudget(1) },
              { id: '2', label: '$$', onClick: () => setMaxBudget(2) },
              { id: '3', label: '$$$', onClick: () => setMaxBudget(3) },
              { id: '4', label: '$$$$', onClick: () => setMaxBudget(4) },
            ]}
            value={`${maxBudget}` || undefined}
          />
        </Accordion>

        <Accordion label="Import Options">
          <PhatDropdown
            inlineHeader
            header="Import category"
            options={[
              { id: 'maintain', label: 'Carry over categories', onClick: () => setImportIntoCategory('maintain') },
              { id: 'auto', label: 'Default card category', onClick: () => setImportIntoCategory('auto') },
              { id: 'maybeboard', label: 'Maybeboard', onClick: () => setImportIntoCategory('maybeboard') },
            ]}
            value={importIntoCategory}
          />
          <label></label>
          <PhatDropdown
            inlineHeader
            header="Import color tag"
            placeholder="Set color label on import"
            options={colorLabels.map(tag => ({
              id: tag.name,
              label: tag.name || 'Default',
              onClick: () => setImportIntoColorTag(tag.name),
            }))}
            value={importIntoColorTag}
          />

          <Checkbox
            onChange={() => setAddOnlyColorIdentOfCommander(!addOnlyColorIdentOfCommander)}
            checked={addOnlyColorIdentOfCommander}
            label="Do not include cards outside commander's color identity"
          />

          <Checkbox
            onChange={() => setSkipDuplicates(!skipDuplicates)}
            checked={skipDuplicates}
            label="Do not include duplicate cards"
          />

          <Checkbox
            onChange={() => setForceQuantityToOne(!forceQuantityToOne)}
            checked={forceQuantityToOne}
            label="Force card quantities to single copy"
          />
        </Accordion>
      </div>

      <div className={styles.packages}>
        {packages?.results.map(deck => (
          <PackageListItem
            openInNewTab
            key={deck.id}
            package={deck}
            onAddPackage={handleAddCardsToDeck}
            onViewPackageDetails={onSelectPackage}
          />
        ))}
      </div>
    </GlobalOverlayStack>
  )
}

export default PackageSelectorList
